import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Scenario, Season, TestType } from 'fuel-benchmark-contracts';
import { Pencil, Plus, Power } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { DataTable, type DataTableColumns } from '../../components/ui/DataTable';
import { InfoCell } from '../../components/ui/InfoCell';
import { Input } from '../../components/ui/Input';
import { Select, type SelectOption } from '../../components/ui/Select';
import { StatusText } from '../../components/ui/StatusText';
import { serverMessage } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { catalogueStatusTone, coreTone, SEASONS, scenarioConditions, TEST_TYPES } from './condition-display';
import { ScenarioSheet } from './ScenarioSheet';

const PAGE_SIZE = 200;

export const ScenariosTab = () => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [testType, setTestType] = useState('');
  const [season, setSeason] = useState('');
  const [status, setStatus] = useState('');
  const [coreOnly, setCoreOnly] = useState(false);
  const [editing, setEditing] = useState<Scenario | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const scenarios = useQuery(
    orpc.scenarios.list.queryOptions({
      input: {
        page: 1,
        pageSize: PAGE_SIZE,
        sortBy: 'code',
        sortDirection: 'asc',
        search: search.trim() === '' ? undefined : search.trim(),
        testType: testType === '' ? undefined : (testType as TestType),
        season: season === '' ? undefined : (season as Season),
        status: status === '' ? undefined : (status as 'ACTIVE' | 'INACTIVE'),
        isCore: coreOnly ? true : undefined,
      },
    }),
  );

  const protocols = useQuery(orpc.protocols.list.queryOptions({ input: { page: 1, pageSize: 100, sortBy: 'code', sortDirection: 'asc' } }));

  const protocolOptions: SelectOption[] = useMemo(
    () =>
      (protocols.data?.items ?? []).map((protocol) => ({ value: protocol.id, label: `${protocol.code} v${protocol.version} — ${protocol.unit}` })),
    [protocols.data],
  );

  const toggleStatus = useMutation(orpc.scenarios.toggleStatus.mutationOptions());

  const onToggle = useCallback(
    async (scenario: Scenario): Promise<void> => {
      setActionError(null);

      try {
        await toggleStatus.mutateAsync({ id: scenario.id });
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: orpc.scenarios.list.key() }),
          queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
        ]);
      } catch (error) {
        setActionError(serverMessage(error) ?? t('errors.saveFailed'));
      }
    },
    [queryClient, t, toggleStatus],
  );

  const columns: DataTableColumns<Scenario> = useMemo(
    () => [
      {
        accessorKey: 'code',
        header: t('conditions.scenarios.columns.code'),
        cell: ({ row }) => <span className="font-mono text-xs text-foreground">{row.original.code}</span>,
      },
      {
        accessorKey: 'testType',
        header: t('conditions.scenarios.columns.testType'),
        cell: ({ row }) => t(`enums.testType.${row.original.testType}`),
      },
      {
        id: 'conditions',
        accessorFn: (scenario) => scenarioConditions(scenario, t),
        header: t('conditions.scenarios.columns.conditions'),
        cell: ({ row }) => <span className="text-foreground">{scenarioConditions(row.original, t)}</span>,
      },
      {
        accessorKey: 'trafficLevel',
        header: t('conditions.scenarios.columns.traffic'),
        // An unspecified traffic level is the ordinary case, so it stays a quiet em dash rather than a gap.
        cell: ({ row }) => (
          <StatusText
            label={row.original.trafficLevel === null ? t('common.notSet') : t(`enums.trafficLevel.${row.original.trafficLevel}`)}
            tone="muted"
          />
        ),
      },
      {
        id: 'protocol',
        accessorFn: (scenario) => scenario.protocolCode,
        header: t('conditions.scenarios.columns.protocol'),
        cell: ({ row }) => (
          <InfoCell
            title={`${row.original.protocolCode} v${row.original.protocolVersion}`}
            subline={row.original.protocolUnit}
          />
        ),
      },
      {
        accessorKey: 'isCore',
        header: t('conditions.scenarios.columns.core'),
        cell: ({ row }) => (
          <StatusText
            label={row.original.isCore ? t('conditions.scenarios.core') : t('conditions.scenarios.supplementary')}
            tone={coreTone(row.original.isCore)}
          />
        ),
      },
      {
        accessorKey: 'status',
        header: t('conditions.scenarios.columns.status'),
        cell: ({ row }) => (
          <StatusText
            label={t(`enums.status.${row.original.status}`)}
            tone={catalogueStatusTone(row.original.status)}
          />
        ),
      },
      {
        id: 'rowActions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              aria-label={t('conditions.scenarios.edit')}
              onClick={() => {
                setEditing(row.original);
                setIsSheetOpen(true);
              }}
            >
              <Pencil className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-label={row.original.status === 'ACTIVE' ? t('conditions.scenarios.toggle.deactivate') : t('conditions.scenarios.toggle.activate')}
              onClick={() => void onToggle(row.original)}
            >
              <Power className="size-3.5" />
            </Button>
          </div>
        ),
      },
    ],
    [onToggle, t],
  );

  const statusOptions: SelectOption[] = [
    { value: 'ACTIVE', label: t('enums.status.ACTIVE') },
    { value: 'INACTIVE', label: t('enums.status.INACTIVE') },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('conditions.scenarios.searchPlaceholder')}
            aria-label={t('conditions.scenarios.searchPlaceholder')}
            className="max-w-xs"
          />
          <Select
            value={testType}
            onChange={(event) => setTestType(event.target.value)}
            options={TEST_TYPES.map((value) => ({ value, label: t(`enums.testType.${value}`) }))}
            placeholder={t('conditions.scenarios.allTestTypes')}
            aria-label={t('conditions.scenarios.allTestTypes')}
            className="w-auto min-w-40"
          />
          <Select
            value={season}
            onChange={(event) => setSeason(event.target.value)}
            options={SEASONS.map((value) => ({ value, label: t(`enums.season.${value}`) }))}
            placeholder={t('conditions.scenarios.allSeasons')}
            aria-label={t('conditions.scenarios.allSeasons')}
            className="w-auto min-w-40"
          />
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            options={statusOptions}
            placeholder={t('conditions.scenarios.allStatuses')}
            aria-label={t('conditions.scenarios.allStatuses')}
            className="w-auto min-w-36"
          />
          <Checkbox
            id="core-only"
            label={t('conditions.scenarios.coreOnly')}
            checked={coreOnly}
            onChange={(event) => setCoreOnly(event.target.checked)}
          />
        </div>

        <Button
          onClick={() => {
            setEditing(null);
            setIsSheetOpen(true);
          }}
        >
          <Plus className="size-4" />
          {t('conditions.scenarios.add')}
        </Button>
      </div>

      {actionError !== null || scenarios.isError ? (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {actionError ?? t('errors.loadFailed')}
        </p>
      ) : null}

      <DataTable
        columns={columns}
        data={scenarios.data?.items ?? []}
        isLoading={scenarios.isPending}
        emptyMessage={t('conditions.scenarios.empty')}
      />

      <ScenarioSheet
        isOpen={isSheetOpen}
        onClose={() => setIsSheetOpen(false)}
        protocolOptions={protocolOptions}
        scenario={editing ?? undefined}
      />
    </div>
  );
};
